import { MenuItem, MenuItemDescription } from '../MenuItem';
import type { SelectOption } from './Select.types';
import { SelectOptionLabel } from './SelectOptionLabel';
import { cn } from '../../lib/styles';

interface RadioMenuItemsProps<T> {
  options: SelectOption<T | null | undefined>[];
  selectedOption?: SelectOption<T | null | undefined> | null;
  className?: string;
  /** Extra classes for each option's label (e.g. to allow wrapping instead of truncating). */
  labelClassName?: string;
  markdownLabels?: boolean;
  /** When true, aligns the radio control to the top of multi-line labels. */
  alignLabelStart?: boolean;
  children?: (children: React.ReactNode, option: SelectOption<T | null | undefined>) => React.ReactNode;
  onValueChange?: (value: T | null | undefined) => void;
  onItemClick?: (option: SelectOption<T | null | undefined>, event: React.MouseEvent) => void;
}

interface RadioMenuItemProps<T> {
  option: SelectOption<T | null | undefined>;
  isSelected: boolean;
  labelClassName?: string;
  markdownLabels?: boolean;
  /** When true, aligns the radio control to the top of multi-line labels. */
  alignLabelStart?: boolean;
  wrapOption?: (children: React.ReactNode, option: SelectOption<T | null | undefined>) => React.ReactNode;
  onSelect: (value: T | null | undefined) => void;
  onClick?: (option: SelectOption<T | null | undefined>, event: React.MouseEvent) => void;
}

export function RadioMenuItem<T>({
  option,
  isSelected,
  labelClassName,
  markdownLabels = false,
  alignLabelStart = false,
  onSelect,
  onClick,
  wrapOption: optionWrapper,
}: RadioMenuItemProps<T>) {
  const handleClick = (event: React.MouseEvent) => {
    onSelect(option.value as T);
    onClick?.(option, event);
  };

  const hasStackedContent = !!option.description;

  const radioIndicator = (
    <div
      className={cn(
        'aspect-square size-4 min-size-4 max-size-4 shrink-0 rounded-full border shadow-xs transition-all outline-none',
        'flex items-center justify-center',
        isSelected ? 'border-text-primary bg-text-primary' : 'border-border-secondary bg-transparent',
        option.disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer group-hover/item:border-bg-inter-primary-active',
        hasStackedContent && 'mt-0.5',
        alignLabelStart && 'mt-0.5',
      )}
    >
      {isSelected && <div className="bg-white rounded-full size-2 min-size-2 max-size-2" />}
    </div>
  );

  const label = (
    <span
      className={cn(
        'font-semibold block w-full text-left',
        markdownLabels ? 'whitespace-normal break-words' : 'truncate',
        option.disabled ? 'text-text-disabled cursor-not-allowed' : 'text-text-primary cursor-pointer',
        labelClassName,
      )}
    >
      <SelectOptionLabel label={option.label} markdown={markdownLabels} />
    </span>
  );

  const menuItem = (
    <MenuItem
      className={cn(
        'w-full group/item flex gap-2 justify-start text-left',
        hasStackedContent || alignLabelStart ? 'items-start' : 'items-center',
      )}
      disabled={option.disabled}
      onClick={handleClick}
    >
      {radioIndicator}

      {hasStackedContent ? (
        <MenuItemDescription title={label} description={option.description} />
      ) : (
        <div className="min-w-0 flex-1 text-left">{label}</div>
      )}

      {option.rightContent}
    </MenuItem>
  );

  if (!optionWrapper) return menuItem;

  return optionWrapper(menuItem, option);
}

export function RadioMenuItems<T>({
  options,
  selectedOption,
  className,
  labelClassName,
  markdownLabels = false,
  alignLabelStart = false,
  onValueChange,
  onItemClick,
  children,
}: RadioMenuItemsProps<T>) {
  const handleChange = (newValue: T | null | undefined) => {
    onValueChange?.(newValue);
  };

  return (
    <div className={cn('gap-0 overflow-hidden', className)}>
      {options.map((option) => {
        const isSelected = selectedOption?.value === option.value;

        return (
          <RadioMenuItem
            key={String(option.value)}
            option={option}
            isSelected={isSelected}
            labelClassName={labelClassName}
            markdownLabels={markdownLabels}
            alignLabelStart={alignLabelStart}
            onSelect={handleChange}
            wrapOption={children}
            onClick={onItemClick}
          />
        );
      })}
    </div>
  );
}
